import {Form, Formik, FormikProps} from 'formik';
import {map} from 'lodash';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import * as Yup from 'yup';

import {
    Accordion,
    AccordionDetails,
    AccordionTitle,
    Button,
    DataWrapper,
    InputFormik,
    UnsavedChangesLeavePrompt
} from 'components';
import {ValidationCode} from 'enums';
import {TRANSLATION_KEYS} from 'locale';
import {SipMessagesSettings} from 'types';
import {apiUtils, dataUtils} from 'utils';

const FIELDS = {
    WELCOME: 'welcome',
    CALLING_AGENT: 'callingAgent',
    FEATURE_DISABLED: 'featureDisabled',
    AGENTS_UNAVAILABLE: 'agentsUnavailable',
    AFTER_HOURS: 'afterHours',
    END_SESSION_SUCCESS: 'endSessionSuccess',
    SESSION_NOT_FOUND: 'sessionNotFound',
    CHATBOT_ERROR: 'chatbotError',
    CLIENT_LEFT: 'clientLeft',
    UNEXPECTED_ERROR: 'unexpectedError',
};

type FormTemplateProps = {
    loading: boolean;
} & FormikProps<SipMessagesSettings>;

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.SESSION.SIP.MESSAGES;
const COMMON_T_KEYS = TRANSLATION_KEYS.COMMON;

const FormTemplate: React.FC<FormTemplateProps> = ({loading, initialValues, submitForm, values}) => {
    const {t} = useTranslation();

    const hasNoChanges = useMemo(() => dataUtils.isEqualValues(values, initialValues), [initialValues, values]);

    return (
        <Form className='tw-relative'>
            <DataWrapper isLoading={loading} hideContentOnLoading={false}>
                <Accordion className='tw-my-2' defaultExpanded>
                    <AccordionTitle>
                        <div className='tw-flex tw-justify-between tw-ts-h5-bold'>
                            {t(T_KEYS.TITLE)}
                        </div>
                    </AccordionTitle>
                    <AccordionDetails>
                        {map(FIELDS, (value, key) => (
                            <InputFormik
                                key={key}
                                name={value}
                                label={t(T_KEYS.LABELS[key])}
                                className='tw-w-full'
                                required
                                spacing='top'
                            />
                        ))}
                        <div className='tw-text-right tw-mt-controls'>
                            <Button
                                color='primary'
                                disabled={hasNoChanges}
                                loading={loading}
                                onClick={submitForm}
                            >
                                {t(COMMON_T_KEYS.BUTTONS.SAVE)}
                            </Button>
                        </div>
                    </AccordionDetails>
                </Accordion>
            </DataWrapper>
            <UnsavedChangesLeavePrompt values={values} initialValues={initialValues} />
        </Form>
    );
};

type MessagesProps = {
    loading: boolean;
    initialValues: SipMessagesSettings;
    onSubmit: (values: SipMessagesSettings) => void;
};

export const Messages: React.FC<MessagesProps> = ({
    loading, initialValues, onSubmit
}) => {
    const {t} = useTranslation();
    const validationSchema = useMemo(() => Yup.object().shape({
        [FIELDS.WELCOME]: Yup.string().required(apiUtils.getErrorMessageByCode(t, {code: ValidationCode.Required})),
        [FIELDS.CALLING_AGENT]: Yup.string().required(apiUtils.getErrorMessageByCode(t, {code: ValidationCode.Required})),
        [FIELDS.FEATURE_DISABLED]: Yup.string().required(apiUtils.getErrorMessageByCode(t, {code: ValidationCode.Required})),
        [FIELDS.CHATBOT_ERROR]: Yup.string().required(apiUtils.getErrorMessageByCode(t, {code: ValidationCode.Required})),
        [FIELDS.END_SESSION_SUCCESS]: Yup.string().required(apiUtils.getErrorMessageByCode(t, {code: ValidationCode.Required})),
        [FIELDS.SESSION_NOT_FOUND]: Yup.string().required(apiUtils.getErrorMessageByCode(t, {code: ValidationCode.Required})),
        [FIELDS.AFTER_HOURS]: Yup.string().required(apiUtils.getErrorMessageByCode(t, {code: ValidationCode.Required})),
        [FIELDS.AGENTS_UNAVAILABLE]: Yup.string().required(apiUtils.getErrorMessageByCode(t, {code: ValidationCode.Required})),
        [FIELDS.UNEXPECTED_ERROR]: Yup.string().required(apiUtils.getErrorMessageByCode(t, {code: ValidationCode.Required})),
    }), [t]);

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            enableReinitialize
            onSubmit={onSubmit}
        >
            {(props) => <FormTemplate {...props} loading={loading} />}
        </Formik>
    );
};